import { useEffect, useState } from 'react';

import {
  Anchor,
  Flex,
  FormHelp,
  Heading,
  IconTrashcanOutline,
  ModalDialog,
  Paragraph,
  Text,
  TextButton,
} from '@zillow/constellation';

import {
  StyledBackgroundImage,
  StyledCarouselContainer,
  StyledCarouselSlot,
  StyledLeftIcon,
  StyledRightIcon,
} from './StyledComponents';
import { TextareaHelper } from './TextareaHelper';

const RIGHT = '-1';
const LEFT = '+1';

export type PhotoProps = {
  caption: string;
  id: string;
  rank: number | string;
  url: string;
  error?: string;
  feedId?: string;
  feedListingId?: string;
  isAppropriate?: boolean;
  thumbUrl?: string;
  errorDto?: {
    errorCode?: string;
    errorMessage?: string;
    replacementPhotoId?: string;
  };
};

const getUrlSrc = (
  photo: PhotoProps,
  getPhotoUrls: (
    isRunningInProd: boolean,
    photoKey: string,
  ) => {
    medium: string;
    thumb: string;
  },
  isRunningInProd: boolean,
) => {
  if (photo?.errorDto?.errorCode) {
    if (photo?.errorDto?.replacementPhotoId) {
      return getPhotoUrls(isRunningInProd, photo?.errorDto?.replacementPhotoId).medium;
    }

    return '';
  }

  return photo.url ? photo.url.replace('http://photo', 'https://photonet') : '';
};

type Props = {
  onClose: () => void;
  idx: number;
  additionalDisabledChecks?: boolean;
  /**
   * disable the caption feature
   *
   * captions are not supported on zillow
   * support date is unknown
   */
  isCaptionDisabled?: boolean;
  photos: Array<PhotoProps>;
  onDeletePhoto: (args: { photoIdAtSlidePosition: string; onComplete: () => void; slidePosition: number }) => void;
  onCaptionUpdate?: (args: { photoId: string; caption: string; onComplete: () => void; slidePosition: number }) => void;
  onClearError: (args: { photoAtSlidePosition: PhotoProps; slidePosition: number }) => void;
  getPhotoUrls?: (
    isRunningInProd: boolean,
    photoKey: string,
  ) => {
    medium: string;
    thumb: string;
  };
  isRunningInProd?: boolean;
};

export const PhotoDetailsModal = ({
  additionalDisabledChecks,
  idx,
  isCaptionDisabled = false,
  onCaptionUpdate,
  onClearError,
  onClose,
  onDeletePhoto,
  photos,
  getPhotoUrls = () => ({ medium: '', thumb: '' }),
  isRunningInProd = false,
}: Props) => {
  const [isCaptionSaving, setIsCaptionSaving] = useState(false);
  const [isPhotoDeleting, setIsPhotoDeleting] = useState(false);
  const [slidePosition, setSlidePosition] = useState(idx);

  const isActionButtonDisabled = additionalDisabledChecks || isCaptionSaving || isPhotoDeleting;

  useEffect(
    () =>
      photos.forEach((image: PhotoProps) => {
        new Image().src = image.url;
      }),
    [],
  );

  const getNextSlideIdx = (direction: string) => {
    let nextSlideIdx;
    if (direction === LEFT) {
      nextSlideIdx = slidePosition - 1 < 0 ? photos.length - 1 : slidePosition - 1;
    } else {
      nextSlideIdx = slidePosition + 1 > photos.length - 1 ? 0 : slidePosition + 1;
    }
    setSlidePosition(nextSlideIdx);
  };

  if (idx < 0 || !photos.length || !photos[slidePosition]) {
    onClose();
    return null;
  }

  return (
    <ModalDialog
      size={{ default: 'fullScreen', md: 'md' }}
      isOpen={true}
      renderHeader={<Heading level={1}>Edit photo</Heading>}
      onClose={onClose}
      renderBody={
        <StyledCarouselContainer>
          <StyledCarouselSlot key={`${photos[slidePosition].id}-carousel-slot`}>
            <Flex
              key={`photo-${photos[slidePosition].id}`}
              display="flex"
              flexDirection={{ default: 'column', lg: 'row' }}
            >
              <Flex flexBasis="50%">
                <StyledBackgroundImage urlSrc={getUrlSrc(photos[slidePosition], getPhotoUrls, isRunningInProd)}>
                  {photos.length > 1 && (
                    <>
                      <StyledLeftIcon size="sm" fontColor="white" onClick={() => getNextSlideIdx(LEFT)}>
                        <title>Previous</title>
                      </StyledLeftIcon>
                      <StyledRightIcon size="sm" fontColor="white" onClick={() => getNextSlideIdx(RIGHT)}>
                        <title>Next</title>
                      </StyledRightIcon>
                    </>
                  )}
                </StyledBackgroundImage>
              </Flex>

              <Flex
                paddingLeft={{ default: 0, lg: 'sm' }}
                marginRight={{ default: 0, lg: 'xs' }}
                paddingTop={{ default: 'sm', lg: 0 }}
                flexBasis="50%"
                flexDirection="column"
              >
                {isCaptionDisabled === false && (
                  <div data-testid="Caption">
                    <Text marginBottom="xs" fontType="bodyHeading" as="p">
                      Caption
                    </Text>

                    <TextareaHelper
                      isDisabled={isCaptionDisabled || isActionButtonDisabled}
                      maxLength="255"
                      initialValue={photos[slidePosition].caption}
                      onClearError={() => onClearError({ photoAtSlidePosition: photos[slidePosition], slidePosition })}
                      handleCaptionBlur={(caption: string) => {
                        setIsCaptionSaving(true);
                        if (onCaptionUpdate) {
                          onCaptionUpdate({
                            photoId: photos[slidePosition].id,
                            caption,
                            onComplete: () => {
                              setIsCaptionSaving(false);
                            },
                            slidePosition: slidePosition,
                          });
                        }
                      }}
                    />

                    {photos[slidePosition].error && (
                      <FormHelp error={true} dangerouslySetInnerHTML={{ __html: photos[slidePosition].error || '' }} />
                    )}
                  </div>
                )}

                <Flex display="flex" marginTop="md">
                  <Flex display="flex" marginRight="md" flexBasis="45%">
                    <TextButton
                      aria-label="Delete photo"
                      data-testid="PhotoDetailsModal-DeletePhotoButton"
                      buttonType="tertiary"
                      disabled={isActionButtonDisabled}
                      onClick={() => {
                        setIsPhotoDeleting(true);
                        onDeletePhoto({
                          photoIdAtSlidePosition: photos[slidePosition].id,
                          onComplete: () => {
                            setIsPhotoDeleting(false);
                            if (photos.length === 1) {
                              setSlidePosition(0);
                            }
                          },
                          slidePosition,
                        });
                      }}
                    >
                      <IconTrashcanOutline fontColor="coolGray500" size="sm">
                        <title>Delete</title>
                      </IconTrashcanOutline>
                      <Text marginLeft="xs" fontColor="textMedium">
                        {isPhotoDeleting ? 'Deleting' : 'Delete'} photo
                      </Text>
                    </TextButton>
                  </Flex>
                </Flex>

                {Boolean(
                  photos[slidePosition]?.isAppropriate === false || photos[slidePosition]?.errorDto?.errorCode,
                ) && (
                  <>
                    <Paragraph marginTop="md" fontColor="red600">
                      {photos[slidePosition]?.errorDto?.errorCode && photos[slidePosition]?.errorDto?.errorMessage
                        ? photos[slidePosition]?.errorDto?.errorMessage
                        : 'This photo is inappropriate or irrelevant for rental listings and will not appear on Zillow Rental Network.'}
                    </Paragraph>
                    <Paragraph marginTop="sm">
                      To understand why this image has been flagged, refer to this{' '}
                      <Anchor
                        target="_blank"
                        href="https://help.zillowrentalmanager.com/hc/en-us/articles/18048174249363-Photos-Flagged-on-Zillow-Rental-Manager-Listings"
                      >
                        help article
                      </Anchor>
                      .
                    </Paragraph>
                  </>
                )}
              </Flex>
            </Flex>
          </StyledCarouselSlot>
        </StyledCarouselContainer>
      }
    />
  );
};
